import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useApiCredentials() {
  return useQuery({
    queryKey: ["api-credentials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("api_credential")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useAddCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credential: {
      label: string;
      provider: string;
      encrypted_secret: string;
      metadata?: any;
    }) => {
      const { data: org } = await supabase
        .from("organization")
        .select("id")
        .limit(1)
        .single();

      if (!org) throw new Error("Organization not found");

      const { data, error } = await supabase
        .from("api_credential")
        .insert({
          ...credential,
          org_id: org.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-credentials"] });
      toast({
        title: "Credential added",
        description: "API credential has been added successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding credential",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteCredential() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentialId: string) => {
      const { error } = await supabase
        .from("api_credential")
        .delete()
        .eq("id", credentialId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-credentials"] });
      toast({
        title: "Credential deleted",
        description: "API credential has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting credential",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
